<template>
  <div>
    <!-- Export Button in the upper right -->
    <div class="d-flex justify-content-end mb-3">
      <button 
        class="btn btn-outline-success"
        @click="exportHeiProfiles"
      >
        <i class="bi bi-file-earmark-arrow-down mr-1"></i>
        Export List
      </button>
    </div>

    <div id="">
      <CCard class="rounded shadow-sm">
        <CCardBody>
          <div class="d-flex justify-content-start align-items-center">
            <div class="col-2 pl-0">
              <input
                type="search"
                class="form-control"
                placeholder="Search by name, code, address"
                v-model="searchValue"
              />
            </div>
  
            <div class="col-2 pl-2">
              <input
                type="text"
                class="form-control"
                placeholder="Reference Number (e.g., HP-2025-123456)"
                v-model="referenceSearch"
                @keyup.enter="searchByReference"
              />
            </div>
  
            <div class="col-2 pl-2">
              <select v-model="selectedAY" class="form-control">
                <option value="">All Academic Years</option>
                <option v-for="ay in uniqueAcademicYears" :key="ay" :value="ay">
                  {{ ay }}
                </option>
              </select>
            </div>
  
            <div class="col-2 pl-2">
              <select v-model="selectedStatus" class="form-control">
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Suspended">Suspended</option>
              </select>
            </div>
  
            <div>
              <button
                class="btn btn-outline-primary text-dark mr-2"
                @click="searchByReference"
                :disabled="referenceSearchLoader || !referenceSearch.trim()"
              >
                <i class="bi bi-search mr-1"></i> Search
              </button>
              <button
                v-if="referenceSearch"
                class="btn btn-outline-warning text-dark mr-2"
                @click="clearReferenceSearch"
                :disabled="referenceSearchLoader"
              >
                <i class="bi bi-x-circle mr-1"></i> Clear
              </button>
              <button
                class="btn btn-outline-secondary text-dark mr-2"
                @click="refreshData"
                :disabled="refreshLoader"
              >
                <i class="bi bi-arrow-clockwise mr-1"></i> Refresh
              </button>
              <div
                v-if="refreshLoader || referenceSearchLoader"
                class="spinner-border spinner-border-sm"
                role="status"
              >
                <span class="sr-only">Loading...</span>
              </div>
            </div>
          </div>
          
          <!-- Search Results Banner -->
          <div v-if="referenceSearch" class="alert alert-info mt-3 mb-0">
            <i class="bi bi-info-circle mr-2"></i>
            Showing search results for reference number: <strong>{{ referenceSearch }}</strong>
            <button 
              class="btn btn-sm btn-outline-secondary ml-2" 
              @click="clearReferenceSearch"
            >
              <i class="bi bi-x-circle mr-1"></i> Show All Profiles
            </button>
          </div>
          
          <div class="table-responsive">
            <table
              id="table-of-hei-profiles"
              class="table table-hover table-bordered table-sm mt-3"
            >
              <thead class="d-md-table-header-group">
                <tr>
                  <th>ID</th>
                  <th>REFERENCE NUMBER</th>
                  <th>HEI CODE</th>
                  <th>HEI NAME</th>
                  <th>HEI ADDRESS</th>
                  <th>HEI STATUS</th>
                  <th>ACADEMIC YEAR</th>
                  <th>PROGRAMS</th>
                  <th>CREATED AT</th>
                  <th>UPDATED AT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  class="text-center"
                  v-if="!filteredList.length && !tableLoader"
                >
                  <td colspan="11">
                    No results found
                  </td>
                </tr>
                <tr class="text-center" v-if="tableLoader">
                  <td colspan="11">
                    <div class="spinner-border spinner-border-sm mr-1"></div>
                    Loading
                  </td>
                </tr>
                <!-- Desktop view - table layout -->
                <tr 
                  v-for="(profile, index) in paginatedList" 
                  :key="profile.id" 
                  class="d-none d-md-table-row"
                >
                  <td>{{ profile.id }}</td>
                  <td>{{ profile.reference_number || 'N/A' }}</td>
                  <td>{{ profile.hei_code }}</td>
                  <td>{{ profile.hei_name }}</td>
                  <td>{{ profile.hei_address }}</td>
                  <td>
                    <span 
                      class="badge" 
                      :class="{
                        'badge-success': profile.hei_status === 'Active',
                        'badge-warning': profile.hei_status === 'Inactive',
                        'badge-danger': profile.hei_status === 'Suspended'
                      }"
                    >
                      {{ profile.hei_status }}
                    </span>
                  </td>
                  <td>{{ profile.academic_year }}</td>
                  <td>
                    <div v-if="profile.programs_data && profile.programs_data.length > 0">
                      <div v-for="(program, index) in profile.programs_data.slice(0, 2)" :key="index" class="mb-1">
                        <small class="text-muted">{{ program.degree_program }}</small>
                      </div>
                      <div v-if="profile.programs_data.length > 2">
                        <small class="text-info">+{{ profile.programs_data.length - 2 }} more</small>
                      </div>
                    </div>
                    <span v-else class="text-muted">No programs</span>
                  </td>
                  <td>
                    <span v-if="profile.created_at">{{
                      profile.created_at | standardDate
                    }}</span>
                  </td>
                  <td>
                    <span v-if="profile.updated_at">{{
                      profile.updated_at | standardDate
                    }}</span>
                  </td>
                  <td class="text-center">
                    <button 
                      class="btn btn-sm btn-outline-info"
                      @click="viewPrograms(profile)"
                      title="View Programs"
                      v-if="profile.programs && profile.programs.length > 0"
                    >
                      <i class="bi bi-list-ul"></i>
                    </button>
                    <button 
                      class="btn btn-sm btn-outline-primary"
                      @click="viewProfile(profile)"
                      title="View Profile Details"
                    >
                      <i class="bi bi-eye"></i>
                    </button>
                    <!-- <button 
                      class="btn btn-sm btn-outline-warning"
                      @click="editProfile(profile)"
                      title="Edit Profile"
                    >
                      <i class="bi bi-pencil-square"></i>
                    </button> -->
                    <button 
                      class="btn btn-sm btn-outline-danger"
                      @click="deleteProfile(profile)"
                      title="Delete Profile"
                      :aria-label="`Delete profile ${index + 1}`"
                    >
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
                
                <!-- Mobile card layout -->
                <div 
                  v-for="(profile, index) in paginatedList" 
                  :key="`mobile-${profile.id}`" 
                  class="card mb-3 d-md-none shadow-sm"
                >
                  <div class="card-body p-3">
                    <h5 class="card-title mb-2">{{ profile.hei_name }}</h5>
                    <h6 class="card-subtitle mb-2 text-muted">{{ profile.hei_code }}</h6>
                    <div class="card-text">
                      <div class="row mb-1">
                        <div class="col-5 text-muted">Reference Number:</div>
                        <div class="col-7">{{ profile.reference_number || 'N/A' }}</div>
                      </div>
                      <div class="row mb-1">
                        <div class="col-5 text-muted">Academic Year:</div>
                        <div class="col-7">{{ profile.academic_year }}</div>
                      </div>
                      <div class="row mb-1">
                        <div class="col-5 text-muted">Status:</div>
                        <div class="col-7">
                          <span 
                            class="badge" 
                            :class="{
                              'badge-success': profile.hei_status === 'Active',
                              'badge-warning': profile.hei_status === 'Inactive',
                              'badge-danger': profile.hei_status === 'Suspended'
                            }"
                          >
                            {{ profile.hei_status }}
                          </span>
                        </div>
                      </div>
                      <div class="row mb-1">
                        <div class="col-5 text-muted">Programs:</div>
                        <div class="col-7">
                          <div v-if="profile.programs_data && profile.programs_data.length > 0">
                            <div v-for="(program, index) in profile.programs_data.slice(0, 2)" :key="index" class="mb-1">
                              <small class="text-muted">{{ program.degree_program }}</small>
                            </div>
                            <div v-if="profile.programs_data.length > 2">
                              <small class="text-info">+{{ profile.programs_data.length - 2 }} more</small>
                            </div>
                          </div>
                          <span v-else class="text-muted">No programs</span>
                        </div>
                      </div>
                      <div class="row mb-1">
                        <div class="col-5 text-muted">Address:</div>
                        <div class="col-7">{{ profile.hei_address }}</div>
                      </div>
                      <div class="mt-2 d-flex justify-content-end">
                        <button 
                          class="btn btn-sm btn-outline-info mr-2"
                          @click="viewPrograms(profile)"
                          title="View Programs"
                          v-if="profile.programs && profile.programs.length > 0"
                        >
                          <i class="bi bi-list-ul"></i> Programs
                        </button>
                        <button 
                          class="btn btn-sm btn-outline-primary mr-2"
                          @click="viewProfile(profile)"
                          title="View Profile Details"
                        >
                          <i class="bi bi-eye"></i> View
                        </button>
                        <button 
                          class="btn btn-sm btn-outline-warning mr-2"
                          @click="editProfile(profile)"
                          title="Edit Profile"
                        >
                          <i class="bi bi-pencil-square"></i> Edit
                        </button>
                        <button 
                          class="btn btn-sm btn-outline-danger"
                          @click="deleteProfile(profile)"
                          title="Delete Profile"
                        >
                          <i class="bi bi-trash"></i> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </tbody>
            </table>
          </div>
          
          <!-- Pagination -->
          <div class="d-flex justify-content-between align-items-center mt-3">
            <div>
              <select v-model="perPage" class="form-control form-control-sm" style="width: 80px">
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </select>
            </div>
            <div>
              <button 
                class="btn btn-sm btn-outline-secondary mr-2" 
                :disabled="currentPage === 1"
                @click="currentPage--"
              >
                Previous
              </button>
              <span class="mr-2">
                Page {{ currentPage }} of {{ totalPages }}
              </span>
              <button 
                class="btn btn-sm btn-outline-secondary" 
                :disabled="currentPage === totalPages"
                @click="currentPage++"
              >
                Next
              </button>
            </div>
          </div>
        </CCardBody>
      </CCard>
    </div>

    <!-- Edit HEI Profile Modal -->
    <div class="modal fade" id="editHeiProfileModal" tabindex="-1" role="dialog" aria-labelledby="editHeiProfileModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editHeiProfileModalLabel">
              <i class="bi bi-pencil-square mr-2"></i>
              Edit HEI Profile
            </h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="editingProfile">
              <form @submit.prevent="saveHeiProfile">
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="font-weight-bold">HEI Code <span class="text-danger">*</span></label>
                      <input 
                        type="text" 
                        class="form-control" 
                        v-model="editingProfile.hei_code"
                        placeholder="Enter HEI code"
                        required
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="font-weight-bold">HEI Name <span class="text-danger">*</span></label>
                      <input 
                        type="text" 
                        class="form-control" 
                        v-model="editingProfile.hei_name"
                        placeholder="Enter HEI name"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label class="font-weight-bold">HEI Address <span class="text-danger">*</span></label>
                  <textarea 
                    class="form-control" 
                    v-model="editingProfile.hei_address"
                    placeholder="Enter HEI address"
                    rows="3"
                    required
                  ></textarea>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="font-weight-bold">HEI Status <span class="text-danger">*</span></label>
                      <select class="form-control" v-model="editingProfile.hei_status" required>
                        <option value="">Select Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Suspended">Suspended</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="font-weight-bold">Academic Year <span class="text-danger">*</span></label>
                      <input 
                        type="text" 
                        class="form-control" 
                        v-model="editingProfile.academic_year"
                        placeholder="e.g., 2024-2025"
                        required
                      />
                    </div>
                  </div>
                </div>

                <!-- Programs Section -->
                <div class="card mt-4">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h6 class="mb-0"><i class="bi bi-list-ul mr-2"></i>Programs</h6>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-success"
                      @click="addNewProgram"
                    >
                      <i class="bi bi-plus-circle mr-1"></i>Add Program
                    </button>
                  </div>
                  <div class="card-body">
                    <div v-if="editingProfile.programs_data && editingProfile.programs_data.length > 0">
                      <div v-for="(program, programIndex) in editingProfile.programs_data" :key="programIndex" class="mb-4">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h6 class="mb-0 text-primary">Program {{ programIndex + 1 }}</h6>
                          <button 
                            type="button" 
                            class="btn btn-sm btn-outline-danger"
                            @click="removeProgram(programIndex)"
                          >
                            <i class="bi bi-trash mr-1"></i>Remove
                          </button>
                        </div>
                        
                        <div class="form-group">
                          <label class="font-weight-bold">Program Name <span class="text-danger">*</span></label>
                          <input 
                            type="text" 
                            class="form-control" 
                            v-model="program.degree_program"
                            placeholder="Enter program name"
                            required
                          />
                        </div>

                        <!-- Years Data -->
                        <div class="table-responsive">
                          <table class="table table-sm table-bordered">
                            <thead class="thead-light">
                              <tr>
                                <th rowspan="2" class="align-middle text-center">Year Level</th>
                                <th rowspan="2" class="align-middle text-center">Semester</th>
                                <th class="text-center">Units</th>
                                <th class="text-center">Tuition/Unit</th>
                                <th class="text-center">Other Fees</th>
                                <th rowspan="2" class="align-middle text-center">Total Fees</th>
                              </tr>
                            </thead>
                            <tbody>
                              <template v-for="(year, yearIndex) in program.years">
                                <tr>
                                  <td rowspan="2" class="align-middle text-center font-weight-bold">
                                    Year {{ yearIndex + 1 }}
                                  </td>
                                  <td class="text-center">1st Sem</td>
                                  <td>
                                    <input 
                                      type="number" 
                                      class="form-control form-control-sm" 
                                      v-model.number="year.first_sem.units"
                                      @input="calculateSemesterTotalEdit(programIndex, yearIndex, 'first_sem')"
                                      min="0"
                                      step="0.5"
                                    />
                                  </td>
                                  <td>
                                    <input 
                                      type="number" 
                                      class="form-control form-control-sm" 
                                      v-model.number="year.first_sem.tuition_per_unit"
                                      @input="calculateSemesterTotalEdit(programIndex, yearIndex, 'first_sem')"
                                      min="0"
                                      step="0.01"
                                    />
                                  </td>
                                  <td>
                                    <input 
                                      type="number" 
                                      class="form-control form-control-sm" 
                                      v-model.number="year.first_sem.other_fees"
                                      @input="calculateSemesterTotalEdit(programIndex, yearIndex, 'first_sem')"
                                      min="0"
                                      step="0.01"
                                    />
                                  </td>
                                  <td class="text-center font-weight-bold">
                                    ₱{{ (year.first_sem.total_fees || 0).toLocaleString() }}
                                  </td>
                                </tr>
                                <tr>
                                  <td class="text-center">2nd Sem</td>
                                  <td>
                                    <input 
                                      type="number" 
                                      class="form-control form-control-sm" 
                                      v-model.number="year.second_sem.units"
                                      @input="calculateSemesterTotalEdit(programIndex, yearIndex, 'second_sem')"
                                      min="0"
                                      step="0.5"
                                    />
                                  </td>
                                  <td>
                                    <input 
                                      type="number" 
                                      class="form-control form-control-sm" 
                                      v-model.number="year.second_sem.tuition_per_unit"
                                      @input="calculateSemesterTotalEdit(programIndex, yearIndex, 'second_sem')"
                                      min="0"
                                      step="0.01"
                                    />
                                  </td>
                                  <td>
                                    <input 
                                      type="number" 
                                      class="form-control form-control-sm" 
                                      v-model.number="year.second_sem.other_fees"
                                      @input="calculateSemesterTotalEdit(programIndex, yearIndex, 'second_sem')"
                                      min="0"
                                      step="0.01"
                                    />
                                  </td>
                                  <td class="text-center font-weight-bold">
                                    ₱{{ (year.second_sem.total_fees || 0).toLocaleString() }}
                                  </td>
                                </tr>
                              </template>
                            </tbody>
                            <tfoot>
                              <tr class="table-info">
                                <td colspan="5" class="text-right font-weight-bold">Program Total:</td>
                                <td class="text-center font-weight-bold">₱{{ calculateProgramTotal(program).toLocaleString() }}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center text-muted">
                      <i class="bi bi-inbox display-4"></i>
                      <p class="mt-2">No programs added yet</p>
                    </div>
                  </div>
                </div>

                <!-- OSF Management Section -->
                <div class="card mt-4">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h6 class="mb-0"><i class="bi bi-list-check mr-2"></i>Other School Fees (OSF) Management</h6>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-primary"
                      @click="addOsfItemToProgram(programIndex)"
                    >
                      <i class="bi bi-plus-circle mr-1"></i>Add OSF Item
                    </button>
                  </div>
                  <div class="card-body">
                    <!-- OSF Items List -->
                    <div class="table-responsive">
                      <table class="table table-sm table-bordered">
                        <thead class="thead-light">
                          <tr>
                            <th>OSF Item</th>
                            <th>Default Amount</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(item, itemIndex) in getCurrentProgramOsfItems()" :key="item.id">
                            <td class="font-weight-bold">{{ item.name }}</td>
                            <td class="text-right">₱{{ item.amount.toLocaleString() }}</td>
                            <td>
                              <span class="badge" :class="item.enabled ? 'badge-success' : 'badge-secondary'">
                                {{ item.enabled ? 'Enabled' : 'Disabled' }}
                              </span>
                            </td>
                            <td>
                              <button 
                                type="button" 
                                class="btn btn-sm btn-outline-primary mr-1"
                                @click="editOsfItem(item, itemIndex)"
                                title="Edit OSF Item"
                              >
                                <i class="bi bi-pencil"></i>
                              </button>
                              <button 
                                type="button" 
                                class="btn btn-sm btn-outline-danger"
                                @click="removeOsfItem(itemIndex)"
                                title="Remove OSF Item"
                              >
                                <i class="bi bi-trash"></i>
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- OSF Breakdown Table -->
                    <div class="mt-4">
                      <h6 class="mb-3">OSF Breakdown by Program</h6>
                      <div v-if="editingProfile.programs_data && editingProfile.programs_data.length > 0">
                        <div v-for="(program, programIndex) in editingProfile.programs_data" :key="programIndex" class="mb-4">
                          <h6 class="text-primary mb-3">{{ program.degree_program || `Program ${programIndex + 1}` }}</h6>
                          <div class="table-responsive">
                            <table class="table table-bordered table-sm">
                              <thead class="thead-light">
                                <tr>
                                  <th>OSF Items</th>
                                  <th v-for="(year, yearIndex) in program.years" :key="yearIndex" colspan="2">
                                    {{ getYearName(yearIndex) }}
                                  </th>
                                </tr>
                                <tr>
                                  <th></th>
                                  <th v-for="(year, yearIndex) in program.years" :key="yearIndex" colspan="2">
                                    <div class="d-flex">
                                      <div class="flex-fill text-center">1st Sem</div>
                                      <div class="flex-fill text-center">2nd Sem</div>
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                        <tbody>
                          <tr v-for="(osfItem, itemIndex) in program.osf_items" :key="osfItem.id">
                            <td class="font-weight-bold">{{ osfItem.name }}</td>
                            <td v-for="(year, yearIndex) in program.years" :key="`osf-first-${programIndex}-${yearIndex}-${itemIndex}`">
                              <div class="d-flex align-items-center">
                                <input 
                                  type="checkbox" 
                                  :checked="year.first_sem.osf_items[itemIndex].enabled"
                                  @change="updateOSFItem(programIndex, yearIndex, 'first_sem', itemIndex, 'enabled', $event.target.checked)"
                                  class="mr-2"
                                />
                                <input 
                                  type="number" 
                                  :value="year.first_sem.osf_items[itemIndex].amount"
                                  @input="updateOSFItem(programIndex, yearIndex, 'first_sem', itemIndex, 'amount', $event.target.value)"
                                  class="form-control form-control-sm"
                                  :disabled="!year.first_sem.osf_items[itemIndex].enabled"
                                />
                              </div>
                            </td>
                            <td v-for="(year, yearIndex) in program.years" :key="`osf-second-${programIndex}-${yearIndex}-${itemIndex}`">
                              <div class="d-flex align-items-center">
                                <input 
                                  type="checkbox" 
                                  :checked="year.second_sem.osf_items[itemIndex].enabled"
                                  @change="updateOSFItem(programIndex, yearIndex, 'second_sem', itemIndex, 'enabled', $event.target.checked)"
                                  class="mr-2"
                                />
                                <input 
                                  type="number" 
                                  :value="year.second_sem.osf_items[itemIndex].amount"
                                  @input="updateOSFItem(programIndex, yearIndex, 'second_sem', itemIndex, 'amount', $event.target.value)"
                                  class="form-control form-control-sm"
                                  :disabled="!year.second_sem.osf_items[itemIndex].enabled"
                                />
                              </div>
                            </td>
                          </tr>
                        </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      <div v-else class="text-center text-muted">
                        <i class="bi bi-inbox display-4"></i>
                        <p class="mt-2">No programs available for OSF breakdown</p>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">
              <i class="bi bi-x-circle mr-1"></i>Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="saveHeiProfile"
              :disabled="savingProfile"
            >
              <i class="bi bi-check-circle mr-1"></i>
              <span v-if="savingProfile">Saving...</span>
              <span v-else>Save Changes</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit OSF Item Modal -->
    <div class="modal fade" id="addOsfModal" tabindex="-1" role="dialog" aria-labelledby="addOsfModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header bg-primary text-white">
            <h5 class="modal-title" id="addOsfModalLabel">
              <i class="bi bi-plus-circle-fill mr-2"></i> {{ editingOsfItem ? 'Edit OSF Item' : 'Add New OSF Item' }}
            </h5>
            <button type="button" class="close text-white" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="editingOsfItem ? updateOsfItem() : addOsfItem()">
              <div class="form-group">
                <label for="osfItemName">OSF Item Name <span class="text-danger">*</span></label>
                <input 
                  type="text" 
                  :value="editingOsfItem ? editingOsfItem.name : newOsfItem.name"
                  @input="editingOsfItem ? editingOsfItem.name = $event.target.value : newOsfItem.name = $event.target.value"
                  class="form-control" 
                  id="osfItemName"
                  placeholder="Enter OSF item name"
                  required
                />
              </div>
              
              <div class="form-group">
                <label for="osfItemAmount">Default Amount (₱)</label>
                <input 
                  type="number" 
                  step="0.01"
                  :value="editingOsfItem ? editingOsfItem.amount : newOsfItem.amount"
                  @input="editingOsfItem ? editingOsfItem.amount = parseFloat($event.target.value) || 0 : newOsfItem.amount = parseFloat($event.target.value) || 0"
                  class="form-control" 
                  id="osfItemAmount"
                  placeholder="0.00"
                  min="0"
                />
              </div>
              
              <div class="form-group">
                <div class="form-check">
                  <input 
                    type="checkbox" 
                    :checked="editingOsfItem ? editingOsfItem.enabled : newOsfItem.enabled"
                    @change="editingOsfItem ? editingOsfItem.enabled = $event.target.checked : newOsfItem.enabled = $event.target.checked"
                    class="form-check-input" 
                    id="osfItemEnabled"
                  />
                  <label class="form-check-label" for="osfItemEnabled">
                    Enable this OSF item by default
                  </label>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">
              <i class="bi bi-x mr-1"></i> Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="editingOsfItem ? updateOsfItem() : addOsfItem()"
            >
              <i class="bi bi-check mr-1"></i> {{ editingOsfItem ? 'Update OSF Item' : 'Add OSF Item' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Programs Modal -->
    <div class="modal fade" id="programsModal" tabindex="-1" role="dialog" aria-labelledby="programsModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-xl" role="document">
        <div class="modal-content">
          <div class="modal-header bg-primary text-white">
            <h5 class="modal-title" id="programsModalLabel">
              <i class="bi bi-list-ul mr-2"></i>Programs - {{ selectedProfile ? selectedProfile.hei_name : '' }}
            </h5>
            <button type="button" class="close text-white" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="selectedProfile">
              <!-- HEI Info -->
              <div class="card mb-4">
                <div class="card-header bg-light">
                  <h6 class="mb-0"><i class="bi bi-building mr-2"></i>HEI Information</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <strong>HEI Code:</strong> {{ selectedProfile.hei_code }}<br>
                      <strong>HEI Name:</strong> {{ selectedProfile.hei_name }}<br>
                      <strong>Academic Year:</strong> {{ selectedProfile.academic_year }}
                    </div>
                    <div class="col-md-6">
                      <strong>Status:</strong> 
                      <span 
                        class="badge ml-1" 
                        :class="{
                          'badge-success': selectedProfile.hei_status === 'Active',
                          'badge-warning': selectedProfile.hei_status === 'Inactive',
                          'badge-danger': selectedProfile.hei_status === 'Suspended'
                        }"
                      >
                        {{ selectedProfile.hei_status }}
                      </span><br>
                      <strong>Reference Number:</strong> {{ selectedProfile.reference_number || 'N/A' }}<br>
                      <strong>Address:</strong> {{ selectedProfile.hei_address }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Programs List -->
              <div v-if="selectedProfile.programs_data && selectedProfile.programs_data.length > 0">
                <h6 class="mb-3"><i class="bi bi-graduation-cap mr-2"></i>Programs ({{ selectedProfile.programs_data.length }})</h6>
                <div v-for="(program, programIndex) in selectedProfile.programs_data" :key="programIndex" class="card mb-3">
                  <div class="card-header bg-info text-white">
                    <h6 class="mb-0">
                      <i class="bi bi-book mr-2"></i>{{ program.degree_program || `Program ${programIndex + 1}` }}
                    </h6>
                  </div>
                  <div class="card-body">
                    <!-- Program Fee Structure -->
                    <div v-if="program.years && program.years.length > 0" class="table-responsive">
                      <table class="table table-sm table-bordered">
                        <thead class="thead-light">
                          <tr>
                            <th rowspan="2" class="align-middle text-center">Year Level</th>
                            <th rowspan="2" class="align-middle text-center">Semester</th>
                            <th class="text-center">Units</th>
                            <th class="text-center">Tuition/Unit</th>
                            <th class="text-center">Other Fees</th>
                            <th rowspan="2" class="align-middle text-center">Total Fees</th>
                          </tr>
                        </thead>
                        <tbody>
                          <template v-for="(year, yearIndex) in program.years">
                            <tr>
                              <td rowspan="2" class="align-middle text-center font-weight-bold">
                                Year {{ yearIndex + 1 }}
                              </td>
                              <td class="text-center">1st Sem</td>
                              <td class="text-center">{{ year.first_sem.units || 0 }}</td>
                              <td class="text-right">₱{{ (year.first_sem.tuition_per_unit || 0).toLocaleString() }}</td>
                              <td class="text-right">₱{{ (year.first_sem.other_fees || 0).toLocaleString() }}</td>
                              <td class="text-center font-weight-bold text-primary">
                                ₱{{ (year.first_sem.total_fees || 0).toLocaleString() }}
                              </td>
                            </tr>
                            <tr>
                              <td class="text-center">2nd Sem</td>
                              <td class="text-center">{{ year.second_sem.units || 0 }}</td>
                              <td class="text-right">₱{{ (year.second_sem.tuition_per_unit || 0).toLocaleString() }}</td>
                              <td class="text-right">₱{{ (year.second_sem.other_fees || 0).toLocaleString() }}</td>
                              <td class="text-center font-weight-bold text-primary">
                                ₱{{ (year.second_sem.total_fees || 0).toLocaleString() }}
                              </td>
                            </tr>
                          </template>
                        </tbody>
                        <tfoot>
                          <tr class="table-info">
                            <td colspan="5" class="text-right font-weight-bold">Program Total:</td>
                            <td class="text-center font-weight-bold text-success">
                              ₱{{ calculateProgramTotal(program).toLocaleString() }}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <div v-else class="text-center text-muted">
                      <i class="bi bi-inbox display-4"></i>
                      <p class="mt-2">No fee structure available</p>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-muted">
                <i class="bi bi-inbox display-4"></i>
                <p class="mt-2">No programs available</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">
              <i class="bi bi-x-circle mr-1"></i>Close
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="viewProfile(selectedProfile)"
              v-if="selectedProfile"
            >
              <i class="bi bi-eye mr-1"></i>View Full Profile
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div class="modal fade" id="confirmationModal" tabindex="-1" role="dialog" aria-labelledby="confirmationModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header bg-warning text-dark">
            <h5 class="modal-title" id="confirmationModalLabel">
              <i class="bi bi-exclamation-triangle-fill mr-2"></i>Confirm Action
            </h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="text-center">
              <i class="bi bi-question-circle display-4 text-warning mb-3"></i>
              <p class="mb-0">{{ confirmationMessage }}</p>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">
              <i class="bi bi-x-circle mr-1"></i>Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-danger" 
              @click="confirmAction"
            >
              <i class="bi bi-check-circle mr-1"></i>Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
  
<script>
import * as XLSX from 'xlsx';

export default {
  name: 'HeiProfilesList',
  data() {
    return {
      searchValue: '',
      referenceSearch: '',
      referenceSearchLoader: false,
      tableLoader: false,
      refreshLoader: false,
      heiProfiles: [],
      currentPage: 1,
      perPage: 10,
      selectedAY: '',
      selectedStatus: '',
      selectedProfile: null,
      editingProfile: null,
      savingProfile: false,
      // OSF Management
      showAddOsfModal: false,
      editingOsfItem: null,
      editingOsfItemIndex: null,
      editingProgramIndex: null,
      newOsfItem: {
        name: '',
        amount: 0.00,
        enabled: true
      },
      // Confirmation Modal
      confirmationMessage: '',
      confirmationAction: null,
      confirmationData: null
    };
  },
  methods: {      
    refreshData() {
      this.refreshLoader = true;
      this.getHeiProfiles();
    },

    searchByReference() {
      if (!this.referenceSearch.trim()) {
        toastr.warning('Please enter a reference number to search');
        return;
      }

      this.referenceSearchLoader = true;
      this.tableLoader = true;

      axios.post('api/hei-profile/search', {
        reference_number: this.referenceSearch.trim()
      })
      .then(response => {
        console.log('Reference search response:', response);
        
        if (response.data && response.data.success) {
          // If search returns a single profile, wrap it in an array
          const searchResults = Array.isArray(response.data.data) 
            ? response.data.data 
            : [response.data.data];
          
          this.heiProfiles = searchResults;
          toastr.success(`Found ${searchResults.length} profile(s) for reference number: ${this.referenceSearch}`);
        } else {
          this.heiProfiles = [];
          toastr.info(`No profiles found for reference number: ${this.referenceSearch}`);
        }
      })
      .catch(error => {
        console.error('Error searching by reference number:', error);
        this.heiProfiles = [];
        
        if (error.response && error.response.status === 404) {
          toastr.info(`No profiles found for reference number: ${this.referenceSearch}`);
        } else {
          toastr.error('Failed to search by reference number');
        }
      })
      .finally(() => {
        this.referenceSearchLoader = false;
        this.tableLoader = false;
      });
    },

    clearReferenceSearch() {
      this.referenceSearch = '';
      this.getHeiProfiles();
    },

    getHeiProfiles() {
      this.tableLoader = true;
      axios
        .get('api/hei-profiles')
        .then((res) => {
          console.log("HEI Profiles response:", res);
          this.heiProfiles = res.data;
          if (this.refreshLoader) {
            toastr.success('Data refreshed successfully');
          }
        })
        .catch((err) => {
          console.error('Error fetching HEI profiles:', err);
          toastr.error('Failed to fetch HEI profiles');
        })
        .finally(() => {
          this.tableLoader = false;
          this.refreshLoader = false;
        });
    },


    editProfile(profile) {
      // Get the most up-to-date profile data from the main array
      const freshProfile = this.heiProfiles.find(p => p.id === profile.id);
      const profileToEdit = freshProfile || profile;
      
      // Deep clone the profile for editing
      this.editingProfile = JSON.parse(JSON.stringify(profileToEdit));
      
      // Ensure programs_data exists
      if (!this.editingProfile.programs_data) {
        this.editingProfile.programs_data = [];
      }
      
      // Initialize OSF items for all existing programs
      this.editingProfile.programs_data.forEach((program, programIndex) => {
        // Initialize program-level OSF items if they don't exist
        if (!program.osf_items) {
          program.osf_items = [];
        }
        
        if (program.years) {
          program.years.forEach((year, yearIndex) => {
            // Initialize osf_items for first_sem if they don't exist
            if (!year.first_sem.osf_items) {
              year.first_sem.osf_items = [];
            }
            
            // Initialize osf_items for second_sem if they don't exist
            if (!year.second_sem.osf_items) {
              year.second_sem.osf_items = [];
            }
            
            // Recalculate other_fees based on OSF items
            year.first_sem.other_fees = this.calculateOSFTotal(year.first_sem.osf_items);
            year.first_sem.total_fees = year.first_sem.total_tuition + year.first_sem.other_fees;
            year.second_sem.other_fees = this.calculateOSFTotal(year.second_sem.osf_items);
            year.second_sem.total_fees = year.second_sem.total_tuition + year.second_sem.other_fees;
          });
        }
      });
      
      // Set default program index for OSF management
      this.editingProgramIndex = 0;
      
      $('#editHeiProfileModal').modal('show');
    },

    deleteProfile(profile) {
      this.confirmationMessage = `Are you sure you want to delete this HEI profile (${profile.hei_name})? This action cannot be undone.`;
      this.confirmationAction = () => this.executeDeleteProfile(profile);
      this.confirmationData = { profile };
      
      $('#confirmationModal').modal('show');
    },

    executeDeleteProfile(profile) {
      axios.delete(`api/hei-profiles/${profile.id}`)
        .then(response => {
          console.log("Delete response", response);
          this.getHeiProfiles();
          toastr.success('HEI profile deleted successfully');
        })
        .catch(error => {
          console.error('Error deleting HEI profile:', error);
          toastr.error('Failed to delete HEI profile');
        });
      
      $('#confirmationModal').modal('hide');
    },

    exportHeiProfiles() {
      if (!this.selectedAY) {
        toastr.warning('Please select an academic year before exporting.');
        return;
      }

      const profiles = this.heiProfiles.filter(p => p.academic_year === this.selectedAY);

      if (!profiles.length) {
        toastr.warning('No schools found for the selected academic year.');
        return;
      }

      const wb = XLSX.utils.book_new();
      const usedNames = {};

      profiles.forEach(profile => {
        const schoolName = profile.hei_name || profile.hei_code || 'School';

        // Sheet name: strip invalid Excel chars, max 31, unique
        let sheetName = schoolName.replace(/[:\\\/\?\*\[\]]/g, '').substring(0, 31).trim();
        const baseKey = sheetName;
        if (usedNames[baseKey] !== undefined) {
          usedNames[baseKey]++;
          const suffix = `(${usedNames[baseKey]})`;
          sheetName = sheetName.substring(0, 31 - suffix.length) + suffix;
        } else {
          usedNames[baseKey] = 1;
        }

        const rows = [];

        // Bold school label (bold applied via cell style below)
        rows.push([schoolName]);
        rows.push([`Academic Year: ${profile.academic_year}`]);
        rows.push([]);

        const programs = profile.programs_data || [];

        programs.forEach((program, programIndex) => {
          const programName = program.degree_program || `Program ${programIndex + 1}`;

          // Program name heading
          rows.push([programName]);

          // Fee Structure
          rows.push(['Fee Structure']);
          rows.push(['Year', 'Semester', 'Units', 'Tuition Per Unit', 'Other Fees', 'Total Fees']);

          if (program.years) {
            program.years.forEach((year, yearIndex) => {
              rows.push([`Year ${yearIndex + 1}`, '1st Sem', year.first_sem.units || 0, year.first_sem.tuition_per_unit || 0, year.first_sem.other_fees || 0, year.first_sem.total_fees || 0]);
              rows.push([`Year ${yearIndex + 1}`, '2nd Sem', year.second_sem.units || 0, year.second_sem.tuition_per_unit || 0, year.second_sem.other_fees || 0, year.second_sem.total_fees || 0]);
            });
          }

          // OSF Management
          if (program.osf_items && program.osf_items.length > 0) {
            rows.push([]);
            rows.push(['OSF Management (Other School Fees)']);
            rows.push(['OSF Item', 'Default Amount', 'Status']);
            program.osf_items.forEach(item => {
              rows.push([item.name, item.amount, item.enabled ? 'Enabled' : 'Disabled']);
            });
          }

          rows.push([]);
        });

        const ws = XLSX.utils.aoa_to_sheet(rows);

        // Bold school name (cell A1) and section labels
        if (!ws['A1']) ws['A1'] = {};
        ws['A1'].s = { font: { bold: true, sz: 14 } };

        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      const fileName = `HEI-Programs-${this.selectedAY}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toastr.success('HEI profiles exported successfully');
    },

    calculateProgramTotal(program) {
      if (!program.years) return 0;
      
      let total = 0;
      program.years.forEach(year => {
        const firstSemTotal = parseFloat((year.first_sem && year.first_sem.total_fees) || 0);
        const secondSemTotal = parseFloat((year.second_sem && year.second_sem.total_fees) || 0);
        total += firstSemTotal + secondSemTotal;
      });
      
      return total;
    },

    calculateAllProgramsTotal() {
      if (!this.selectedProfile || !this.selectedProfile.programs_data) return 0;
      
      let grandTotal = 0;
      this.selectedProfile.programs_data.forEach(program => {
        grandTotal += this.calculateProgramTotal(program);
      });
      
      return grandTotal;
    },

    // Edit HEI Profile Methods
    addNewProgram() {
      if (!this.editingProfile.programs_data) {
        this.editingProfile.programs_data = [];
      }
      
      const newProgram = {
        degree_program: '',
        osf_items: [], // Initialize empty OSF items array for this program
        years: [
          {
            first_sem: { 
              units: 0, 
              tuition_per_unit: 0, 
              total_tuition: 0, 
              other_fees: 0, 
              total_fees: 0,
              osf_items: []
            },
            second_sem: { 
              units: 0, 
              tuition_per_unit: 0, 
              total_tuition: 0, 
              other_fees: 0, 
              total_fees: 0,
              osf_items: []
            }
          },
          {
            first_sem: { 
              units: 0, 
              tuition_per_unit: 0, 
              total_tuition: 0, 
              other_fees: 0, 
              total_fees: 0,
              osf_items: []
            },
            second_sem: { 
              units: 0, 
              tuition_per_unit: 0, 
              total_tuition: 0, 
              other_fees: 0, 
              total_fees: 0,
              osf_items: []
            }
          },
          {
            first_sem: { 
              units: 0, 
              tuition_per_unit: 0, 
              total_tuition: 0, 
              other_fees: 0, 
              total_fees: 0,
              osf_items: []
            },
            second_sem: { 
              units: 0, 
              tuition_per_unit: 0, 
              total_tuition: 0, 
              other_fees: 0, 
              total_fees: 0,
              osf_items: []
            }
          },
          {
            first_sem: { 
              units: 0, 
              tuition_per_unit: 0, 
              total_tuition: 0, 
              other_fees: 0, 
              total_fees: 0,
              osf_items: []
            },
            second_sem: { 
              units: 0, 
              tuition_per_unit: 0, 
              total_tuition: 0, 
              other_fees: 0, 
              total_fees: 0,
              osf_items: []
            }
          }
        ]
      };
      
      this.editingProfile.programs_data.push(newProgram);
    },

    removeProgram(programIndex) {
      const program = this.editingProfile.programs_data[programIndex];
      const programName = program ? program.degree_program || `Program ${programIndex + 1}` : 'this program';
      
      this.confirmationMessage = `Are you sure you want to remove "${programName}"? This action cannot be undone.`;
      this.confirmationAction = () => this.executeRemoveProgram(programIndex);
      this.confirmationData = { programIndex, programName };
      
      $('#confirmationModal').modal('show');
    },

    executeRemoveProgram(programIndex) {
      this.editingProfile.programs_data.splice(programIndex, 1);
      toastr.success('Program removed successfully');
      $('#confirmationModal').modal('hide');
    },

    calculateSemesterTotalEdit(programIndex, yearIndex, semester) {
      const program = this.editingProfile.programs_data[programIndex];
      const year = program.years[yearIndex];
      const semesterData = year[semester];
      
      // Calculate total tuition
      semesterData.total_tuition = (semesterData.units || 0) * (semesterData.tuition_per_unit || 0);
      
      // Calculate total fees
      semesterData.total_fees = semesterData.total_tuition + (semesterData.other_fees || 0);
      
      // Force reactivity update
      this.$forceUpdate();
    },

    saveHeiProfile() {
      if (!this.editingProfile) {
        toastr.error('No profile data to save');
        return;
      }

      // Basic validation
      if (!this.editingProfile.hei_code || !this.editingProfile.hei_name || 
          !this.editingProfile.hei_address || !this.editingProfile.hei_status || 
          !this.editingProfile.academic_year) {
        toastr.error('Please fill in all required fields');
        return;
      }

      this.savingProfile = true;

      // Prepare the data for API call
      const updateData = {
        hei_code: this.editingProfile.hei_code,
        hei_name: this.editingProfile.hei_name,
        hei_address: this.editingProfile.hei_address,
        hei_status: this.editingProfile.hei_status,
        academic_year: this.editingProfile.academic_year,
        programs: this.editingProfile.programs_data || []
      };

      axios.put(`api/hei-profiles/${this.editingProfile.id}`, updateData)
        .then(response => {
          console.log('HEI Profile updated successfully:', response);
          
          // Update the main list
          const profileIndex = this.heiProfiles.findIndex(p => p.id === this.editingProfile.id);
          if (profileIndex !== -1) {
            // Update with the response data if available, otherwise use the edited data
            const updatedProfile = response.data && response.data.data ? response.data.data : this.editingProfile;
            
            // Use Vue.set to ensure reactivity
            this.$set(this.heiProfiles, profileIndex, { ...updatedProfile });
            
            // Also update selectedProfile if it's the same profile being edited
            if (this.selectedProfile && this.selectedProfile.id === this.editingProfile.id) {
              this.selectedProfile = { ...updatedProfile };
            }
          }
          
          // Close the modal
          $('#editHeiProfileModal').modal('hide');
          
          // Force Vue to re-render the component
          this.$forceUpdate();
          
          toastr.success('HEI profile updated successfully');
        })
        .catch(error => {
          console.error('Error updating HEI profile:', error);
          toastr.error('Failed to update HEI profile');
        })
        .finally(() => {
          this.savingProfile = false;
        });
    },

    // OSF Management Methods
    getYearName(yearIndex) {
      const yearNames = ['First Year', 'Second Year', 'Third Year', 'Fourth Year'];
      return yearNames[yearIndex] || `Year ${yearIndex + 1}`;
    },

    calculateOSFTotal(osfItems) {
      return osfItems.reduce((total, item) => {
        return total + (item.enabled ? parseFloat(item.amount) || 0 : 0);
      }, 0);
    },

    getCurrentProgramOsfItems() {
      if (!this.editingProfile || !this.editingProfile.programs_data || this.editingProfile.programs_data.length === 0) {
        return [];
      }
      // Return OSF items from the currently selected program
      if (this.editingProgramIndex !== null && this.editingProfile.programs_data[this.editingProgramIndex]) {
        return this.editingProfile.programs_data[this.editingProgramIndex].osf_items || [];
      }
      // Fallback to first program if no specific program is selected
      const firstProgram = this.editingProfile.programs_data[0];
      return firstProgram.osf_items || [];
    },

    updateOSFItem(programIndex, yearIndex, semester, osfItemIndex, field, value) {
      const year = this.editingProfile.programs_data[programIndex].years[yearIndex];
      const sem = year[semester];
      
      if (sem.osf_items && sem.osf_items[osfItemIndex]) {
        if (field === 'enabled') {
          sem.osf_items[osfItemIndex].enabled = value;
        } else if (field === 'amount') {
          sem.osf_items[osfItemIndex].amount = parseFloat(value) || 0;
        }
        
        // Recalculate other_fees
        sem.other_fees = this.calculateOSFTotal(sem.osf_items);
        sem.total_fees = sem.total_tuition + sem.other_fees;
        
        // Force reactivity update
        this.$forceUpdate();
      }
    },

    addOsfItemToProgram(programIndex) {
      this.editingProgramIndex = programIndex;
      this.showAddOsfModal = true;
    },

    addOsfItem() {
      if (!this.newOsfItem.name.trim()) {
        toastr.warning('Please enter OSF item name');
        return;
      }
      
      if (this.editingProgramIndex === null) {
        toastr.error('No program selected for adding OSF item');
        return;
      }
      
      const newItem = {
        id: Date.now(), // Use timestamp as unique ID
        name: this.newOsfItem.name.trim(),
        amount: parseFloat(this.newOsfItem.amount) || 0,
        enabled: this.newOsfItem.enabled
      };
      
      const program = this.editingProfile.programs_data[this.editingProgramIndex];
      
      // Initialize osf_items array if it doesn't exist
      if (!program.osf_items) {
        program.osf_items = [];
      }
      
      // Add to program's OSF items
      program.osf_items.push(newItem);
      
      // Add to all years and semesters in this program
      if (program.years) {
        program.years.forEach((year, yearIndex) => {
          // Initialize osf_items if they don't exist
          if (!year.first_sem.osf_items) {
            year.first_sem.osf_items = [];
          }
          if (!year.second_sem.osf_items) {
            year.second_sem.osf_items = [];
          }
          
          year.first_sem.osf_items.push({
            id: newItem.id,
            name: newItem.name,
            amount: newItem.amount,
            enabled: newItem.enabled
          });
          year.second_sem.osf_items.push({
            id: newItem.id,
            name: newItem.name,
            amount: newItem.amount,
            enabled: newItem.enabled
          });
          
          // Recalculate fees
          year.first_sem.other_fees = this.calculateOSFTotal(year.first_sem.osf_items);
          year.first_sem.total_fees = year.first_sem.total_tuition + year.first_sem.other_fees;
          year.second_sem.other_fees = this.calculateOSFTotal(year.second_sem.osf_items);
          year.second_sem.total_fees = year.second_sem.total_tuition + year.second_sem.other_fees;
        });
      }
      
      // Reset form
      this.newOsfItem = {
        name: '',
        amount: 0.00,
        enabled: true
      };
      this.editingProgramIndex = null;
      
      $('#addOsfModal').modal('hide');
      toastr.success('OSF item added successfully');
    },

    editOsfItem(item, itemIndex) {
      this.editingOsfItem = { ...item };
      this.editingOsfItemIndex = itemIndex;
      $('#addOsfModal').modal('show');
    },

    updateOsfItem() {
      if (!this.editingOsfItem.name.trim()) {
        toastr.warning('Please enter OSF item name');
        return;
      }
      
      if (this.editingProgramIndex === null || this.editingOsfItemIndex === null) {
        toastr.error('No program or item selected for editing');
        return;
      }
      
      const program = this.editingProfile.programs_data[this.editingProgramIndex];
      
      // Update the program's OSF items
      if (program.osf_items && program.osf_items[this.editingOsfItemIndex]) {
        program.osf_items[this.editingOsfItemIndex] = { ...this.editingOsfItem };
        
        // Update all years and semesters in this program
        if (program.years) {
          program.years.forEach((year, yearIndex) => {
            if (year.first_sem.osf_items && year.first_sem.osf_items[this.editingOsfItemIndex]) {
              year.first_sem.osf_items[this.editingOsfItemIndex] = {
                id: this.editingOsfItem.id,
                name: this.editingOsfItem.name,
                amount: this.editingOsfItem.amount,
                enabled: this.editingOsfItem.enabled
              };
              year.first_sem.other_fees = this.calculateOSFTotal(year.first_sem.osf_items);
              year.first_sem.total_fees = year.first_sem.total_tuition + year.first_sem.other_fees;
            }
            
            if (year.second_sem.osf_items && year.second_sem.osf_items[this.editingOsfItemIndex]) {
              year.second_sem.osf_items[this.editingOsfItemIndex] = {
                id: this.editingOsfItem.id,
                name: this.editingOsfItem.name,
                amount: this.editingOsfItem.amount,
                enabled: this.editingOsfItem.enabled
              };
              year.second_sem.other_fees = this.calculateOSFTotal(year.second_sem.osf_items);
              year.second_sem.total_fees = year.second_sem.total_tuition + year.second_sem.other_fees;
            }
          });
        }
        
        toastr.success('OSF item updated successfully');
      }
      
      this.editingOsfItem = null;
      this.editingOsfItemIndex = null;
      this.editingProgramIndex = null;
      $('#addOsfModal').modal('hide');
    },

    removeOsfItem(itemIndex) {
      if (this.editingProgramIndex === null) {
        toastr.error('No program selected for removing OSF item');
        return;
      }
      
      const program = this.editingProfile.programs_data[this.editingProgramIndex];
      const item = program.osf_items[itemIndex];
      const itemName = item ? item.name : 'this OSF item';
      
      this.confirmationMessage = `Are you sure you want to remove "${itemName}"? This will remove it from this program.`;
      this.confirmationAction = () => this.executeRemoveOsfItem(itemIndex);
      this.confirmationData = { itemIndex, itemName };
      
      $('#confirmationModal').modal('show');
    },

    executeRemoveOsfItem(itemIndex) {
      if (this.editingProgramIndex === null) {
        toastr.error('No program selected for removing OSF item');
        return;
      }
      
      const program = this.editingProfile.programs_data[this.editingProgramIndex];
      
      if (program.osf_items && program.osf_items[itemIndex]) {
        const removedItem = program.osf_items[itemIndex];
        
        // Remove from program's OSF items
        program.osf_items.splice(itemIndex, 1);
        
        // Remove from all years and semesters in this program
        if (program.years) {
          program.years.forEach((year, yearIndex) => {
            if (year.first_sem.osf_items) {
              year.first_sem.osf_items = year.first_sem.osf_items.filter(osf => osf.id !== removedItem.id);
              year.first_sem.other_fees = this.calculateOSFTotal(year.first_sem.osf_items);
              year.first_sem.total_fees = year.first_sem.total_tuition + year.first_sem.other_fees;
            }
            if (year.second_sem.osf_items) {
              year.second_sem.osf_items = year.second_sem.osf_items.filter(osf => osf.id !== removedItem.id);
              year.second_sem.other_fees = this.calculateOSFTotal(year.second_sem.osf_items);
              year.second_sem.total_fees = year.second_sem.total_tuition + year.second_sem.other_fees;
            }
          });
        }
        
        toastr.success('OSF item removed successfully');
      }
      
      this.editingProgramIndex = null;
      $('#confirmationModal').modal('hide');
    },

    confirmAction() {
      if (this.confirmationAction) {
        this.confirmationAction();
      }
    },

    viewProfile(profile) {
      // Navigate to the TOSF HEI profile view
      this.$router.push({
        name: 'TosfHeiProfile',
        params: { 
          id: profile.id,
          heiProfile: profile
        }
      });
    },

    viewPrograms(profile) {
      // Set the selected profile and show programs modal
      this.selectedProfile = profile;
      $('#programsModal').modal('show');
    },

    syncQueryParams() {
      const query = { ...this.$route.query };
      if (this.selectedAY) query.selectedAY = this.selectedAY;
      else delete query.selectedAY;
      if (this.selectedStatus) query.selectedStatus = this.selectedStatus;
      else delete query.selectedStatus;
      this.$router.replace({ query }).catch(() => {});
    },
  },
  computed: {
    uniqueAcademicYears() {
      return [...new Set(this.heiProfiles.map(item => item.academic_year))].sort();
    },
    
    filteredList() {
      if (!this.searchValue && !this.selectedAY && !this.selectedStatus) {
        return this.heiProfiles;
      }
      
      return this.heiProfiles.filter(profile => {
        const matchesSearch = !this.searchValue || [
          profile.hei_name,
          profile.hei_code,
          profile.hei_address
        ].some(field => 
          field?.toLowerCase().includes(this.searchValue.toLowerCase().trim())
        );

        const matchesAY = !this.selectedAY || 
          profile.academic_year === this.selectedAY;

        const matchesStatus = !this.selectedStatus || 
          profile.hei_status === this.selectedStatus;

        return matchesSearch && matchesAY && matchesStatus;
      });
    },

    paginatedList() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredList.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredList.length / this.perPage);
    }
  },
  watch: {
    searchValue() {
      this.currentPage = 1;
    },
    perPage() {
      this.currentPage = 1;
    },
    selectedAY(val) {
      this.currentPage = 1;
      this.syncQueryParams();
    },
    selectedStatus(val) {
      this.currentPage = 1;
      this.syncQueryParams();
    }
  },
  mounted() {
    if (this.$route.query.selectedAY) {
      this.selectedAY = this.$route.query.selectedAY;
    }
    if (this.$route.query.selectedStatus) {
      this.selectedStatus = this.$route.query.selectedStatus;
    }
    this.getHeiProfiles();
  },
};
</script>
  
<style scoped>
#table-of-hei-profiles tr {
  cursor: pointer !important;
}

.bi-arrow-clockwise {
  font-size: 1rem;
}

.btn i {
  font-size: 0.875rem;
}

/* Responsive styles */
.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 767.98px) {
  .card {
    cursor: pointer !important;
  }
  
  /* Adjust filter controls for mobile */
  .d-flex.justify-content-start.align-items-center {
    flex-wrap: wrap;
  }
  
  .d-flex.justify-content-start.align-items-center > div {
    margin-bottom: 0.5rem;
  }
  
  /* Stack search buttons on mobile */
  .d-flex.justify-content-start.align-items-center > div:last-child {
    width: 100%;
    margin-top: 0.5rem;
  }
  
  .d-flex.justify-content-start.align-items-center > div:last-child button {
    margin-bottom: 0.25rem;
  }
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

/* Edit HEI Profile Modal Styles */
#editHeiProfileModal .form-control {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

#editHeiProfileModal .form-control:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

#editHeiProfileModal .form-control-sm {
  font-size: 0.875rem;
}

#editHeiProfileModal .table td {
  vertical-align: middle;
}

#editHeiProfileModal .table input {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

#editHeiProfileModal .table input:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

#editHeiProfileModal .card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

#editHeiProfileModal .table-info {
  background-color: #d1ecf1;
}

#editHeiProfileModal .text-danger {
  color: #dc3545 !important;
}
</style>
